// Server-side blog utilities that run in loaders
import * as fs from 'node:fs/promises'
import * as path from 'node:path'
import { compile } from '@mdx-js/mdx'
import { createServerFn } from '@tanstack/react-start'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import type { BlogPost } from './blog'

const slugSchema = (input: unknown) => {
  if (typeof input !== 'string') {
    throw new Error('Slug must be a string')
  }
  if (input.length === 0) {
    throw new Error('Slug cannot be empty')
  }
  if (!/^[a-zA-Z0-9\-_]+$/.test(input)) {
    throw new Error('Slug contains invalid characters')
  }
  return input
}

function parseDate(dateString: string): Date {
  // Handle DD/MM/YYYY format
  const [day, month, year] = dateString.split('/')
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
}

export const getPostsServer = createServerFn({ method: "GET" }).handler(async (): Promise<BlogPost[]> => {
  const postsDirectory = path.join(process.cwd(), 'posts')

  try {
    const matter = (await import('gray-matter')).default
    const filenames = (await fs.readdir(postsDirectory)).filter(name => !name.includes("veille-tech"))
    const posts = await Promise.all(
      filenames
        .filter(name => name.endsWith('.mdx'))
        .map(async (name) => {
          const filePath = path.join(postsDirectory, name)
          const fileContents = await fs.readFile(filePath, 'utf8')
          const { data, content } = matter(fileContents)

          // Calculate reading time (assuming ~200 words per minute)
          const words = content.trim().split(/\s+/).length
          const readingTime = Math.ceil(words / 200)

          // Create slug from filename
          const slug = name.replace(/\.mdx$/, '')

          // Compile MDX
          let compiledMDX: string | undefined
          try {
            const compiled = await compile(content, {
              outputFormat: 'function-body',
              development: false, // Always use production JSX runtime
              jsxImportSource: 'react',
              remarkPlugins: [remarkGfm],
              rehypePlugins: [rehypeHighlight],
            })
            compiledMDX = String(compiled)
          } catch (error) {
            console.error(`Error compiling MDX for ${slug}:`, error)
          }

          return {
            slug,
            title: data.title || '',
            description: data.description || '',
            date: data.date || '',
            image: data.image || '',
            tags: data.tags ? data.tags.split(' ').filter(Boolean) : [],
            author: data.author || '',
            lang: data.lang || 'en',
            content,
            compiledMDX,
            readingTime,
          } as BlogPost
        })
    )

    // Sort posts by date (newest first)
    return posts.sort((a, b) => {
      const dateA = parseDate(a.date)
      const dateB = parseDate(b.date)
      return dateB.getTime() - dateA.getTime()
    })
  } catch (error) {
    console.error('Error reading posts:', error)
    return []
  }
})

export const getPostBySlugServer = createServerFn({ method: "GET" })
  .validator(slugSchema)
  .handler(async ({ data: slug }): Promise<BlogPost | null> => {
    try {
      const matter = (await import('gray-matter')).default
      const filePath = path.join(process.cwd(), 'posts', `${slug}.mdx`)
      const fileContents = await fs.readFile(filePath, 'utf8')
      const { data, content } = matter(fileContents)

      // Calculate reading time
      const words = content.trim().split(/\s+/).length
      const readingTime = Math.ceil(words / 200)

      // Compile MDX
      let compiledMDX: string | undefined
      try {
        const compiled = await compile(content, {
          outputFormat: 'function-body',
          development: false, // Always use production JSX runtime
          jsxImportSource: 'react',
          remarkPlugins: [remarkGfm],
          rehypePlugins: [rehypeHighlight],
        })
        compiledMDX = String(compiled)
      } catch (error) {
        console.error(`Error compiling MDX for ${slug}:`, error)
      }

      return {
        slug,
        title: data.title || '',
        description: data.description || '',
        date: data.date || '',
        image: data.image || '',
        tags: data.tags ? data.tags.split(' ').filter(Boolean) : [],
        author: data.author || '',
        lang: data.lang || 'en',
        content,
        compiledMDX,
        readingTime,
      }
    } catch (error) {
      console.error(`Error reading post ${slug}:`, error)
      return null
    }
  })


export const getTechWatchPostsServer = createServerFn({ method: "GET" })
  .validator(slugSchema)
  .handler(async ({ data: slug }): Promise<BlogPost[]> => {
    const postsDirectory = path.join(process.cwd(), 'posts')

    try {
      const matter = (await import('gray-matter')).default
      const filenames = await fs.readdir(postsDirectory)
      const posts = await Promise.all(
        filenames
          .filter(name => name.endsWith('.mdx'))
          .filter(name => name.includes(slug))
          .map(async (name) => {
            const filePath = path.join(postsDirectory, name)
            const fileContents = await fs.readFile(filePath, 'utf8')
            const { data, content } = matter(fileContents)

            // Calculate reading time (assuming ~200 words per minute)
            const words = content.trim().split(/\s+/).length
            const readingTime = Math.ceil(words / 200)

            // Create slug from filename
            const slug = name.replace(/\.mdx$/, '')

            // Compile MDX
            let compiledMDX: string | undefined
            try {
              const compiled = await compile(content, {
                outputFormat: 'function-body',
                development: false, // Always use production JSX runtime
                jsxImportSource: 'react',
                remarkPlugins: [remarkGfm],
                rehypePlugins: [rehypeHighlight],
              })
              compiledMDX = String(compiled)
            } catch (error) {
              console.error(`Error compiling MDX for ${slug}:`, error)
            }

            return {
              slug,
              title: data.title || '',
              description: data.description || '',
              date: data.date || '',
              image: data.image || '',
              tags: data.tags ? data.tags.split(' ').filter(Boolean) : [],
              author: data.author || '',
              lang: data.lang || 'en',
              content,
              compiledMDX,
              readingTime,
            } as BlogPost
          })
      )

      // Sort posts by date (newest first)
      return posts.sort((a, b) => {
        const dateA = parseDate(a.date)
        const dateB = parseDate(b.date)
        return dateB.getTime() - dateA.getTime()
      })
    } catch (error) {
      console.error('Error reading posts:', error)
      return []
    }
  })

export const getMemesServer = createServerFn({ method: "GET" }).handler(async (): Promise<string[]> => {
  const memesDirectory = path.join(process.cwd(), 'public', 'memes')
  try {
    const filenames = await fs.readdir(memesDirectory)
    return filenames
      .filter(name => name.endsWith('.webp'))
      .map(name => `/memes/${name}`)
  } catch (error) {
    console.error('Error reading memes:', error)
    return []
  }
})
